<template>
  <div id="header-wrapper">
    <header>
      <el-row type="flex" align="middle" justify="space-between" id="nav-row">
        <el-col id="nav-col-left" :span="9">
          <div class="nav-wrapper">
            <img src="../assets/logo_new.png" width="180px" />
          </div>
        </el-col>
        <el-col id="nav-col-right" type="flex" :span="15">
          <!-- CP (Batch 3): top bar aligned to the CPA-Production shared-layer reference — all items live in one
               horizontal flex el-menu (justify:end, height 50px) so the instance title and switcher sit on one
               line and the instance sub-menu renders; PA-specific bits (sortedInstances, Help) are kept. -->
          <el-menu
            theme="dark"
            class="el-menu-demo"
            mode="horizontal"
            active-text-color="#ffd04b"
            menu-trigger="click"
            :ellipsis="false"
            style="display: flex; justify-content: end; border-bottom: none; height: 50px;"
          >
            <el-menu-item index="instance-info" class="custom-border custom-padding">
              <div>
                <div
                  class="nav-wrapper info-text"
                  v-if="user.isLoggedIn && production && production.name && $route.path !== '/login'"
                >
                  {{ production.name || "" }}
                </div>
                <div
                  class="nav-wrapper info-text"
                  v-else-if="user.isLoggedIn && company && company.name && $route.path !== '/login'"
                >
                  {{ company.name || "" }}
                </div>
                <div
                  class="nav-wrapper info-text"
                  v-else-if="user.isLoggedIn && !availableInstances.length && $route.path !== '/login'"
                >
                  {{ activeInstance.url || "" }}
                </div>
              </div>
            </el-menu-item>
            <el-menu-item
              index="1"
              class="custom-padding"
              v-if="!user.isLoggedIn && $route.path !== '/login'"
            >
              <router-link to="/login">Login</router-link>
            </el-menu-item>
            <el-sub-menu
              v-if="user.isLoggedIn && availableInstances.length"
              index="2"
              class="nav-item"
            >
              <template v-slot:title>
                {{ activeInstance.name || activeInstance.url }}
              </template>
              <el-menu-item
                v-for="app in sortedInstances"
                :key="app.url"
                @click="checkIfReAuthNeeded(app.url)"
                class="nav-menu-item"
                index="2-1"
              >
                {{ app.name || app.url }}
              </el-menu-item>
              <el-menu-item
                @click="addInstance"
                class="nav-menu-item"
                index="2-2"
              >
                Sign in to new instance
              </el-menu-item>
            </el-sub-menu>
            <el-sub-menu index="3" v-if="user.isLoggedIn" class="nav-item">
              <template v-slot:title>{{ user.username }}</template>
              <el-menu-item class="nav-menu-item" index="3-1" @click="goToChangePassword()">
                Change Password
              </el-menu-item>
              <el-menu-item class="nav-menu-item" index="3-2" @click="logout()">
                Logout
              </el-menu-item>
            </el-sub-menu>
            <el-menu-item index="4" v-if="$route.path !== '/login'" class="custom-padding">
              <a href="https://help.revolutiones.com/en/collections/3555235-probooks" target="_blank">Help</a>
            </el-menu-item>
          </el-menu>
        </el-col>
      </el-row>
    </header>
    <ReAuthModal
      v-if="user.isLoggedIn"
      :url="url"
      @reauthSuccess="switchAppInstance"
    ></ReAuthModal>
    <AuthModal
      v-if="user.isLoggedIn"
      @authSuccess="switchAppInstance"
    ></AuthModal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import ReAuthModal from '@/components/ReAuthModal.vue'
import AuthModal from '@/components/AuthModal.vue'

export default {
  name: 'AppHeader',
  components: { ReAuthModal, AuthModal },
  data () {
    return {
      reAuthSuccess: false,
      url: ''
    }
  },
  methods: {
    /**
     * Method for testing with invalid token
     */
    killToken () {
      this.$store.dispatch('sabotageToken')
    },
    /**
     * Triggers logout procedure
     */
    async logout () {
      try {
        await this.$store.dispatch('logout')
        this.$router.push('/login')
      } catch (e) {
        console.log(e)
      }
    },
    async goToChangePassword () {
      this.$router.push('/change_password')
    },
    /**
     * Checks if authentication is needed to switch app instances
     */
    checkIfReAuthNeeded (url) {
      const targetApp = this.instances.find((v) => v.url === url)
      if (!targetApp.token || this.checkIfTokenIsExpired(targetApp)) {
        this.url = targetApp.url
        this.$store.dispatch('changeModalVisibility', {
          entityType: 'reauthModal',
          visible: true
        })
      } else {
        this.url = ''
        this.$store.dispatch('changeModalVisibility', {
          entityType: 'reauthModal',
          visible: false
        })
        this.switchAppInstance(targetApp.url)
      }
    },
    /**
     * Checks if an app instance's token is expired
     * @param targetApp   Target app instance
     */
    checkIfTokenIsExpired (targetApp) {
      return targetApp.token && Math.round(Date.now() / 1000) >= targetApp.exp
    },
    /**
     * Allows a user to switch between app instances. Clears the store (except for auth state), fetches settings,
     * and reloads data for the new instance. If the user is already in the posting program, fetches new data;
     * otherwise, routes the user to the posting program.
     * @param url    The url of the instance to switch to
     */
    async switchAppInstance (url) {
      try {
        const instance = this.instances.find((v) => v.url === url)
        if (instance) {
          await this.$store.dispatch('clearState')
          await this.$store.dispatch('switchActiveAppInstance', instance)
          await this.$store.dispatch('fetchUiSetting', { name: 'AR_ENABLED' })
          await this.$store.dispatch('fetchUiSetting', { name: 'TRANSACTION_ENTRY_OPTIONS' })
          await this.$store.dispatch('fetchUiSetting', {
            name: 'PAYROLL_ENABLED'
          })
          await this.$store.dispatch('fetchUiSetting', {
            name: 'TRIAL_BALANCE_REPORT_DEFAULT_SETTINGS'
          })
          await this.$store.dispatch('fetchUiSetting', {
            name: 'GL_REPORT_DEFAULT_SETTINGS'
          })
          await this.$store.dispatch('fetchHomePageTextUiSetting')

          await this.$parent.loadData()
          if (this.$route.name === 'Home') {
            this.$emitter.emit('reloadHomeData')
          }
          this.$router.push({ name: 'Home' })
        }
      } catch (e) {
        console.log(e)
      }
    },
    addInstance () {
      this.$store.dispatch('changeModalVisibility', {
        entityType: 'authModal',
        visible: true
      })
    }
  },
  computed: {
    ...mapGetters({
      user: 'getUser',
      instances: 'getInstances',
      production: 'getDefaultProduction',
      company: 'getDefaultCompany',
      activeInstance: 'getActiveInstance',
      availableInstances: 'getAvailableInstances',
      loading: 'getSwitchingActiveAppInstanceStatus',
      getReauthStatus: 'getReauthStatus',
      transactions: 'getTransactionsToBePosted'
    }),
    sortedInstances () {
      if (!this.availableInstances || !this.availableInstances.length) return []
      return this.availableInstances.slice().sort((a, b) => {
        if (a.name && b.name) {
          return a.name.localeCompare(b.name)
        } else if (a.name && !b.name) {
          return -1
        } else if (!a.name && b.name) {
          return 1
        } else {
          return a.url.localeCompare(b.url)
        }
      })
    }
  },
  watch: {
    // activeInstance () {
    //   if (this.user.isLoggedIn) {
    //     this.$store.dispatch('clearProductionState')
    //     this.$store.dispatch('fetchProductionsWithQuery', null)
    //   }
    // }
  }
}
</script>

<style scoped>
/* Match item height to the 50px menu bar so the instance name and the
   dropdown titles share one baseline (default --el-menu-horizontal-height is
   60px, which pushed the dropdown titles ~7px off the instance name). */
.el-menu-demo {
  --el-menu-horizontal-height: 50px;
}
.info-text {
  color: #000;
  font-size: 14px;
  line-height: 50px;
}
#header-wrapper {
  background: #fff;
  padding: 5px 0;
  border-bottom-width: 12px;
  border-bottom-style: solid;
  border-image: linear-gradient(135deg, #55d9a3 0%, #006a9c 100%);
  border-image-slice: 1;
}
#nav-row {
  padding: 0 12px;
  height: 40px;
}
#nav-col-left {
  text-align: left;
}
#nav-col-left h1 {
  margin: 0;
  font-family: "Roboto Condensed", "Helvetica", Arial, sans-serif;
}
#nav-col-right {
  text-align: right;
}
/* header .el-sub-menu__title {
  color: #fff !important;
} */
.nav-wrapper {
  display: inline-block;
  vertical-align: middle;
  font-size: 14px;
}
.nav-wrapper img {
  vertical-align: middle;
}
.nav-wrapper:nth-of-type(2) {
  padding: 0 6px;
}
header {
  vertical-align: middle;
  color: #fff;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 100;
  position: relative;
}
.nav-item {
  padding: 4px 12px;
}
.custom-padding {
  padding-bottom: 6px !important;
}
.nav-menu-item {
  text-align: center;
}
.el-menu--dark .el-menu-item,
.el-menu--dark .el-sub-menu__title {
  color: #000 !important;
}
</style>
<style>
#nav-col-right .el-menu--dark .el-menu-item,
#nav-col-right .el-menu--dark .el-sub-menu__title {
  color: #000 !important;
}
.el-menu--horizontal > .el-menu-item:hover,
.el-menu--horizontal > .el-sub-menu.is-active .el-sub-menu__title,
.el-menu--horizontal > .el-sub-menu:hover .el-sub-menu__title {
  border-bottom: 5px solid #22909f !important;
}
</style>